/**
 * EF-CLIENT-PORTAL-UI-V1
 * Client Project Detail Page
 * 
 * Features:
 * - Project details
 * - Shifts for this project
 * - Assigned crew
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Calendar,
  Clock,
  MapPin,
  Users,
  Briefcase,
  User,
  Pencil,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import {
  ShiftRequirementsEditor,
  RequirementsSummaryInline,
} from "../../components/client/ShiftRequirementsEditor";
import { useI18n } from "../../i18n/I18nProvider";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// EVENTSFLO-CLIENT-PORTAL-PROJECT-SHIFT-CHANGE-CANCEL-REQUESTS-V1
const PROJECT_CHANGEABLE_STATUSES = new Set([
  "draft", "confirmed", "scheduled", "active",
]);
const PROJECT_TERMINAL_STATUSES = new Set([
  "completed", "archived", "cancelled",
]);

const REQUEST_TYPE_LABELS = {
  project_change: "Project change requested",
  project_cancellation: "Project cancellation requested",
  shift_change: "Shift change requested",
  shift_cancellation: "Shift cancellation requested",
};
const STATUS_LABELS = {
  submitted: "Awaiting review",
  under_review: "Under review",
  accepted: "Accepted",
  rejected: "Rejected",
  applied: "Applied",
  cancelled_by_client: "Cancelled by you",
};
const STATUS_STYLES = {
  submitted: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  under_review: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  accepted: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  rejected: "bg-red-500/10 text-red-700 border-red-500/30",
  applied: "bg-purple-500/10 text-purple-700 border-purple-500/30",
  cancelled_by_client: "bg-zinc-500/10 text-zinc-700 border-zinc-500/30",
};

export default function ClientProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getToken } = useOutletContext();
  const { t } = useI18n();

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [error, setError] = useState(null);
  // EVENTSFLO-CLIENT-PORTAL-PROJECT-SHIFT-CHANGE-CANCEL-REQUESTS-V1
  const [changeRequests, setChangeRequests] = useState([]);
  const [reqDialog, setReqDialog] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [submittingReq, setSubmittingReq] = useState(false);
  // EVENTSFLO-CLIENT-SHIFT-CHANGE-REQUEST-VENUE-LOCATION-DETAILS-V1
  // Venues + their locations visible to the calling client portal user.
  const [clientVenues, setClientVenues] = useState([]);

  const fetchProjectData = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    setLoading(true);
    try {
      // Fetch project details
      const projectRes = await fetch(`${API_URL}/api/client/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!projectRes.ok) {
        if (projectRes.status === 404) {
          setError("Project not found");
          return;
        }
        throw new Error("Failed to fetch project");
      }

      const projectData = await projectRes.json();
      setProject(projectData);

      // Fetch shifts for this project
      const shiftsRes = await fetch(
        `${API_URL}/api/client/shifts?project_id=${id}&page_size=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (shiftsRes.ok) {
        const shiftsData = await shiftsRes.json();
        setShifts(shiftsData.data || []);
      }
    } catch (err) {
      console.error("Project fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, getToken]);

  // EVENTSFLO-CLIENT-PORTAL-PROJECT-SHIFT-CHANGE-CANCEL-REQUESTS-V1
  const fetchChangeRequests = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const r = await fetch(
        `${API_URL}/api/client/change-requests?project_id=${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (r.ok) {
        const data = await r.json();
        setChangeRequests(data.data || []);
      }
    } catch (e) {
      console.error("Change requests fetch error:", e);
    }
  }, [id, getToken]);

  // EVENTSFLO-CLIENT-SHIFT-CHANGE-REQUEST-VENUE-LOCATION-DETAILS-V1
  const fetchClientVenues = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const r = await fetch(`${API_URL}/api/client/venues`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        setClientVenues(data.venues || []);
      }
    } catch (e) {
      console.error("Client venues fetch error:", e);
    }
  }, [getToken]);

  useEffect(() => {
    fetchProjectData();
    fetchChangeRequests();
    fetchClientVenues();
  }, [fetchProjectData, fetchChangeRequests, fetchClientVenues]);

  // EVENTSFLO-CLIENT-PORTAL-PROJECT-SHIFT-CHANGE-CANCEL-REQUESTS-V1
  const projectStatusLc = (project?.status || "").toLowerCase();
  const projectActionsAllowed =
    project && !PROJECT_TERMINAL_STATUSES.has(projectStatusLc);
  const openProjectRequest = (type) => {
    setReqDialog({
      type,
      shift_id: null,
      message: "",
      requested_changes: type === "project_change"
        ? {
            project_name: project?.name || "",
            start_date: project?.start_date || "",
            end_date: project?.end_date || "",
            notes: "",
          }
        : {},
    });
    setConfirmCancel(false);
  };
  const openShiftRequest = (type, shift) => {
    setReqDialog({
      type,
      shift_id: shift.id,
      shift_summary: `${shift.name || shift.id} • ${shift.date} ${shift.start_time}–${shift.end_time}`,
      message: "",
      requested_changes: type === "shift_change"
        ? {
            // EVENTSFLO-CLIENT-SHIFT-CHANGE-REQUEST-VENUE-LOCATION-DETAILS-V1
            date: shift.date || "",
            start_time: shift.start_time || "",
            end_time: shift.end_time || "",
            staff_required: shift.staff_required || 1,
            venue_id: shift.venue_id || "",
            venue_location_id: shift.venue_location_id || "",
            activity: shift.name || "",
            role: shift.role || "",
            notes: shift.notes || "",
            // EVENTSFLO-CLIENT-PORTAL-SHIFT-REQUIREMENTS-LINE-ITEMS-V1
            // Deep-clone so edits in dialog don't mutate the live shift
            // object that we re-render in the list behind the dialog.
            requirements: Array.isArray(shift.client_requirements)
              ? shift.client_requirements.map((r) => ({ ...r }))
              : [],
          }
        : {},
    });
    setConfirmCancel(false);
  };
  const submitChangeRequest = async () => {
    if (!reqDialog || !project) return;
    const isCancel = reqDialog.type.endsWith("_cancellation");
    if (isCancel && !confirmCancel) {
      toast.error(t("clientPortal:projectDetail.confirmCancellation"));
      return;
    }
    if (!reqDialog.message.trim() && isCancel) {
      toast.error(t("clientPortal:projectDetail.cancellationReasonRequired"));
      return;
    }
    setSubmittingReq(true);
    try {
      const token = getToken();
      const body = {
        request_type: reqDialog.type,
        project_id: project.id,
        shift_id: reqDialog.shift_id || null,
        client_message: reqDialog.message,
        requested_changes: reqDialog.requested_changes || {},
      };
      const r = await fetch(`${API_URL}/api/client/change-requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        const detail = data.detail;
        const msg = typeof detail === "string"
          ? detail
          : detail?.message || "Failed to submit request";
        throw new Error(msg);
      }
      toast.success(
        isCancel
          ? "Cancellation request submitted. Our team will review and confirm."
          : "Change request submitted. Our team will review and confirm.",
      );
      setReqDialog(null);
      fetchChangeRequests();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmittingReq(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
      case "confirmed":
        return "bg-green-500/10 text-green-500 border-green-500/20";
      case "draft":
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500/20";
      case "completed":
        return "bg-blue-500/10 text-blue-500 border-blue-500/20";
      case "cancelled":
        return "bg-red-500/10 text-red-500 border-red-500/20";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate("/client/projects")} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Projects
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/client/projects")}
          className="shrink-0"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold truncate" data-testid="project-title">
              {project.name}
            </h1>
            <Badge variant="outline" className={getStatusColor(project.status)}>
              {project.status}
            </Badge>
          </div>
          {project.reference_number && (
            <p className="text-sm text-muted-foreground">{project.reference_number}</p>
          )}
        </div>
        {/* EVENTSFLO-CLIENT-PORTAL-PROJECT-SHIFT-CHANGE-CANCEL-REQUESTS-V1 */}
        {projectActionsAllowed && (
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => openProjectRequest("project_change")}
              data-testid="request-project-change-btn"
            >
              <Pencil className="h-4 w-4 mr-1" />
              Request project change
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openProjectRequest("project_cancellation")}
              data-testid="request-project-cancel-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Request project cancellation
            </Button>
          </div>
        )}
      </div>

      {/* EVENTSFLO-CLIENT-PORTAL-PROJECT-SHIFT-CHANGE-CANCEL-REQUESTS-V1
          Existing change/cancel requests for this project. */}
      {changeRequests.length > 0 && (
        <Card data-testid="change-requests-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Your change & cancellation requests
              <Badge variant="secondary" className="ml-1">
                {changeRequests.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            {changeRequests.map((cr) => (
              <div
                key={cr.id}
                className="rounded border border-border p-2 text-xs"
                data-testid={`change-request-${cr.id}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">
                    {REQUEST_TYPE_LABELS[cr.request_type] || cr.request_type}
                  </span>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${STATUS_STYLES[cr.status] || ""}`}
                  >
                    {STATUS_LABELS[cr.status] || cr.status}
                  </Badge>
                  {cr.shift_date && (
                    <span className="text-muted-foreground">
                      • shift {cr.shift_date}
                    </span>
                  )}
                </div>
                {cr.client_message && (
                  <p className="text-muted-foreground mt-1 whitespace-pre-line">
                    {cr.client_message}
                  </p>
                )}
                {/* EVENTSFLO-CLIENT-CHANGE-REQUESTS-ADMIN-APPLY-WORKFLOW-V1 */}
                {cr.status === "rejected" && cr.rejection_reason && (
                  <div
                    className="mt-2 rounded border border-red-500/40 bg-red-500/10 p-2"
                    data-testid={`change-request-${cr.id}-rejection`}
                  >
                    <p className="text-[10px] uppercase tracking-wide text-red-300">
                      Rejected by iCrew Services
                    </p>
                    <p className="text-foreground whitespace-pre-wrap">
                      {cr.rejection_reason}
                    </p>
                  </div>
                )}
                {cr.status === "applied" && (
                  <div
                    className="mt-2 rounded border border-green-500/40 bg-green-500/10 p-2"
                    data-testid={`change-request-${cr.id}-applied`}
                  >
                    <p className="text-[10px] uppercase tracking-wide text-green-300">
                      Applied by iCrew Services
                    </p>
                    <p className="text-foreground">
                      Your requested change has been applied. The project /
                      shift now reflects the update.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* SECTION 1: Project Info (Top) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Briefcase className="h-4 w-4" />
            Project Details
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 rounded-lg bg-accent/30">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Start</p>
              <p className="font-medium text-sm mt-0.5">
                {project.start_date || "—"}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-accent/30">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">End</p>
              <p className="font-medium text-sm mt-0.5">
                {project.end_date || "—"}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-accent/30">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t("clientPortal:projectDetail.shifts")}</p>
              <p className="font-medium text-sm mt-0.5">
                {project.total_shifts || 0}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-accent/30" data-testid="project-crew-summary">
              {/* EVENTSFLO-CLIENT-PORTAL-CREW-COUNT-LAYER-AUDIT-FIX-V1
                  Show requested crew first (what the client asked for), and
                  surface assigned crew only as a secondary line when non-zero
                  so the client never sees a misleading "Crew: 0". */}
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Crew required
              </p>
              <p
                className="font-medium text-sm mt-0.5"
                data-testid="project-crew-required-value"
              >
                {(() => {
                  const total = project.crew_required_total || 0;
                  const mn = project.crew_required_per_shift_min;
                  const mx = project.crew_required_per_shift_max;
                  if (total <= 0) return "—";
                  if (mn != null && mx != null && mn === mx) {
                    return `${total} (${mn} per shift)`;
                  }
                  if (mn != null && mx != null) {
                    return `${total} (${mn}–${mx} per shift)`;
                  }
                  return String(total);
                })()}
              </p>
              {(project.assigned_crew_count || 0) > 0 && (
                <p
                  className="text-[10px] text-muted-foreground mt-0.5"
                  data-testid="project-assigned-crew-value"
                >
                  Assigned crew: {project.assigned_crew_count}
                </p>
              )}
            </div>
          </div>

          {project.venue_name && (
            <div className="mt-4 flex items-start gap-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">{project.venue_name}</p>
                {project.venue_address && (
                  <p className="text-muted-foreground text-xs">{project.venue_address}</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* SECTION 2: Shifts (Middle) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            {t("clientPortal:projectDetail.shifts")}
            <Badge variant="secondary" className="ml-1">{shifts.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {shifts.length === 0 ? (
            <div className="py-8 text-center">
              <Calendar className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">{t("clientPortal:projectDetail.noShiftsScheduled")}</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {shifts.map((shift) => (
                <div
                  key={shift.id}
                  className="py-4 first:pt-0 last:pb-0"
                  data-testid={`shift-${shift.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium truncate">{shift.name}</p>
                        <Badge variant="outline" className={`text-[10px] ${getStatusColor(shift.status)}`}>
                          {shift.status}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1 font-medium text-foreground">
                          <Calendar className="h-3 w-3" />
                          {shift.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {shift.start_time} – {shift.end_time}
                        </span>
                        {shift.venue_name && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {shift.venue_name}
                          </span>
                        )}
                        {/* EVENTSFLO-CLIENT-PORTAL-CREW-COUNT-LAYER-AUDIT-FIX-V1 */}
                        {(shift.staff_required ?? null) != null && (
                          <span
                            className="flex items-center gap-1"
                            data-testid={`shift-${shift.id}-crew-required`}
                          >
                            <User className="h-3 w-3" />
                            Crew required: {shift.staff_required}
                            {(shift.staff_assigned || 0) > 0 && (
                              <span className="text-[10px] ml-1 opacity-70">
                                · Assigned: {shift.staff_assigned}
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* EVENTSFLO-CLIENT-PORTAL-PROJECT-SHIFT-CHANGE-CANCEL-REQUESTS-V1 */}
                    {projectActionsAllowed &&
                      !PROJECT_TERMINAL_STATUSES.has((shift.status || "").toLowerCase()) && (
                        <div className="flex flex-col gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-[11px]"
                            onClick={() => openShiftRequest("shift_change", shift)}
                            data-testid={`request-shift-change-${shift.id}`}
                          >
                            <Pencil className="h-3 w-3 mr-1" />
                            Request change
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-[11px]"
                            onClick={() => openShiftRequest("shift_cancellation", shift)}
                            data-testid={`request-shift-cancel-${shift.id}`}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Request cancel
                          </Button>
                        </div>
                      )}
                  </div>

                  {/* Assigned Crew - clearer layout */}
                  {shift.assigned_crew?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {shift.assigned_crew.map((crew) => (
                        <div
                          key={crew.id}
                          className="inline-flex items-center gap-1.5 text-xs bg-accent px-2 py-1 rounded-full"
                        >
                          <User className="h-3 w-3 text-muted-foreground" />
                          <span className="font-medium">{crew.name}</span>
                          {crew.role && (
                            <span className="text-muted-foreground">• {crew.role}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* EVENTSFLO-CLIENT-PORTAL-SHIFT-REQUIREMENTS-LINE-ITEMS-V1 */}
                  {Array.isArray(shift.client_requirements) &&
                    shift.client_requirements.length > 0 && (
                      <div
                        className="mt-3"
                        data-testid={`shift-${shift.id}-requirements`}
                      >
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                          Requested requirements
                          <span className="ml-1 normal-case opacity-70">
                            (not yet a confirmed booking)
                          </span>
                        </div>
                        <RequirementsSummaryInline
                          value={shift.client_requirements}
                        />
                      </div>
                    )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* EVENTSFLO-CLIENT-PORTAL-PROJECT-SHIFT-CHANGE-CANCEL-REQUESTS-V1
          Dialog for project/shift change & cancellation requests. */}
      <Dialog
        open={!!reqDialog}
        onOpenChange={(open) => {
          if (!open) {
            setReqDialog(null);
            setConfirmCancel(false);
          }
        }}
      >
        <DialogContent
          className="max-w-md"
          data-testid="change-request-dialog"
        >
          {reqDialog && (
            <>
              <DialogHeader>
                <DialogTitle data-testid="change-request-dialog-title">
                  {REQUEST_TYPE_LABELS[reqDialog.type] || "Request"}
                </DialogTitle>
                <DialogDescription>
                  {reqDialog.type === "project_cancellation" &&
                    "Submit a cancellation request for this project. Our team will review and confirm."}
                  {reqDialog.type === "shift_cancellation" &&
                    "Submit a cancellation request for this shift. Our team will review and confirm."}
                  {reqDialog.type === "project_change" &&
                    "Tell us what you'd like to change on this project."}
                  {reqDialog.type === "shift_change" &&
                    "Tell us what you'd like to change on this shift."}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3">
                {reqDialog.shift_summary && (
                  <div className="text-xs text-muted-foreground bg-accent/40 rounded p-2">
                    Shift: <span className="font-medium text-foreground">{reqDialog.shift_summary}</span>
                  </div>
                )}

                {reqDialog.type === "project_change" && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">{t("clientPortal:projectDetail.projectName")}</Label>
                      <Input
                        data-testid="cr-project-name-input"
                        value={reqDialog.requested_changes.project_name || ""}
                        onChange={(e) =>
                          setReqDialog({
                            ...reqDialog,
                            requested_changes: {
                              ...reqDialog.requested_changes,
                              project_name: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div />
                    <div>
                      <Label className="text-xs">{t("clientPortal:projectDetail.startDate")}</Label>
                      <Input
                        type="date"
                        data-testid="cr-project-start-input"
                        value={reqDialog.requested_changes.start_date || ""}
                        onChange={(e) =>
                          setReqDialog({
                            ...reqDialog,
                            requested_changes: {
                              ...reqDialog.requested_changes,
                              start_date: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t("clientPortal:projectDetail.endDate")}</Label>
                      <Input
                        type="date"
                        data-testid="cr-project-end-input"
                        value={reqDialog.requested_changes.end_date || ""}
                        onChange={(e) =>
                          setReqDialog({
                            ...reqDialog,
                            requested_changes: {
                              ...reqDialog.requested_changes,
                              end_date: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                )}

                {reqDialog.type === "shift_change" && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">{t("clientPortal:projectDetail.date")}</Label>
                      <Input
                        type="date"
                        data-testid="cr-shift-date-input"
                        value={reqDialog.requested_changes.date || ""}
                        onChange={(e) =>
                          setReqDialog({
                            ...reqDialog,
                            requested_changes: {
                              ...reqDialog.requested_changes,
                              date: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div>
                      {/* EVENTSFLO-CLIENT-PORTAL-CREW-COUNT-LAYER-AUDIT-FIX-V1
                          Client-facing label is "Crew required"; payload field
                          name remains staff_required for backend compat. */}
                      <Label className="text-xs">{t("clientPortal:projectDetail.crewRequired")}</Label>
                      <Input
                        type="number"
                        min={1}
                        data-testid="cr-shift-staff-input"
                        value={reqDialog.requested_changes.staff_required ?? 1}
                        onChange={(e) =>
                          setReqDialog({
                            ...reqDialog,
                            requested_changes: {
                              ...reqDialog.requested_changes,
                              staff_required: parseInt(e.target.value || "1", 10),
                            },
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t("clientPortal:projectDetail.startTime")}</Label>
                      <Input
                        type="time"
                        data-testid="cr-shift-start-input"
                        value={reqDialog.requested_changes.start_time || ""}
                        onChange={(e) =>
                          setReqDialog({
                            ...reqDialog,
                            requested_changes: {
                              ...reqDialog.requested_changes,
                              start_time: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t("clientPortal:projectDetail.endTime")}</Label>
                      <Input
                        type="time"
                        data-testid="cr-shift-end-input"
                        value={reqDialog.requested_changes.end_time || ""}
                        onChange={(e) =>
                          setReqDialog({
                            ...reqDialog,
                            requested_changes: {
                              ...reqDialog.requested_changes,
                              end_time: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    </div>

                    {/* EVENTSFLO-CLIENT-SHIFT-CHANGE-REQUEST-VENUE-LOCATION-DETAILS-V1 */}
                    <div className="grid grid-cols-2 gap-2 pt-1 border-t border-border/40">
                      <div>
                        <Label className="text-xs">Venue</Label>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          data-testid="cr-shift-venue-input"
                          value={reqDialog.requested_changes.venue_id || ""}
                          onChange={(e) =>
                            setReqDialog({
                              ...reqDialog,
                              requested_changes: {
                                ...reqDialog.requested_changes,
                                venue_id: e.target.value || "",
                                // Reset location whenever venue changes
                                venue_location_id: "",
                              },
                            })
                          }
                        >
                          <option value="">— Select venue —</option>
                          {clientVenues.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.name}
                              {v.is_shared ? " (shared)" : ""}
                            </option>
                          ))}
                          {reqDialog.requested_changes.venue_id &&
                            !clientVenues.find(
                              (v) => v.id === reqDialog.requested_changes.venue_id,
                            ) && (
                              <option value={reqDialog.requested_changes.venue_id}>
                                Current venue (not in your visible list)
                              </option>
                            )}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Location / meeting point</Label>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          data-testid="cr-shift-location-input"
                          value={reqDialog.requested_changes.venue_location_id || ""}
                          onChange={(e) =>
                            setReqDialog({
                              ...reqDialog,
                              requested_changes: {
                                ...reqDialog.requested_changes,
                                venue_location_id: e.target.value || "",
                              },
                            })
                          }
                          disabled={!reqDialog.requested_changes.venue_id}
                        >
                          <option value="">— Select location —</option>
                          {(
                            clientVenues.find(
                              (v) => v.id === reqDialog.requested_changes.venue_id,
                            )?.locations || []
                          ).map((loc) => (
                            <option key={loc.id} value={loc.id}>
                              {loc.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Activity / task</Label>
                        <Input
                          data-testid="cr-shift-activity-input"
                          placeholder="e.g. Set-up — Montage"
                          value={reqDialog.requested_changes.activity || ""}
                          onChange={(e) =>
                            setReqDialog({
                              ...reqDialog,
                              requested_changes: {
                                ...reqDialog.requested_changes,
                                activity: e.target.value,
                              },
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Crew role / task</Label>
                        <Input
                          data-testid="cr-shift-role-input"
                          placeholder="e.g. Crew / Road"
                          value={reqDialog.requested_changes.role || ""}
                          onChange={(e) =>
                            setReqDialog({
                              ...reqDialog,
                              requested_changes: {
                                ...reqDialog.requested_changes,
                                role: e.target.value,
                              },
                            })
                          }
                        />
                      </div>
                    </div>

                    {/* EVENTSFLO-CLIENT-PORTAL-SHIFT-REQUIREMENTS-LINE-ITEMS-V1 */}
                    <ShiftRequirementsEditor
                      shiftIndex={0}
                      testIdPrefix="cr-shift"
                      value={reqDialog.requested_changes.requirements || []}
                      onChange={(next) =>
                        setReqDialog({
                          ...reqDialog,
                          requested_changes: {
                            ...reqDialog.requested_changes,
                            requirements: next,
                          },
                        })
                      }
                    />
                  </div>
                )}

                <div>
                  <Label className="text-xs">
                    {reqDialog.type.endsWith("_cancellation")
                      ? "Reason for cancellation"
                      : "Message to our team"}
                  </Label>
                  <Textarea
                    rows={3}
                    data-testid="cr-message-input"
                    placeholder={
                      reqDialog.type.endsWith("_cancellation")
                        ? "Please tell us why you need to cancel..."
                        : "Anything else our team should know..."
                    }
                    value={reqDialog.message}
                    onChange={(e) =>
                      setReqDialog({ ...reqDialog, message: e.target.value })
                    }
                  />
                </div>

                {reqDialog.type.endsWith("_cancellation") && (
                  <label
                    className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer"
                    data-testid="cr-confirm-cancel-label"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={confirmCancel}
                      onChange={(e) => setConfirmCancel(e.target.checked)}
                      data-testid="cr-confirm-cancel-checkbox"
                    />
                    <span>
                      I understand this submits a cancellation request to the
                      EventsFlo team for review. No project or shift will be
                      cancelled until they confirm.
                    </span>
                  </label>
                )}
              </div>

              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setReqDialog(null);
                    setConfirmCancel(false);
                  }}
                  disabled={submittingReq}
                  data-testid="cr-cancel-btn"
                >
                  Close
                </Button>
                <Button
                  onClick={submitChangeRequest}
                  disabled={submittingReq}
                  data-testid="cr-submit-btn"
                >
                  {submittingReq && (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  )}
                  Submit request
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
